import React, { useState, useEffect, useMemo } from 'react';
import { OI_SLIDER_MAX } from '../../../utils/liquidityRule';
import { XMarkIcon } from '@heroicons/react/24/outline';
import FilterSections from './FilterSections';
import { useStrategy } from '../../../contexts/StrategyContext';
import {
  AdvancedFilterModalProps,
  AdvancedFilters,
  DistributionResponse,
  COVERED_CALL_DEFAULT_FILTERS,
} from './types';
import './AdvancedFilterModal.css';

interface FilterApiParams {
  min_iv_rank?: number;
  max_iv_rank?: number;
  min_rsi?: number;
  max_rsi?: number;
  min_net_margin?: number;
  max_net_margin?: number;
  max_debt_equity?: number;
  min_annualized_yield?: number;
  max_annualized_yield?: number;
  min_dividend_yield?: number;
  max_dividend_yield?: number;
  min_spread_pct?: number;
  max_spread_pct?: number;
  sectors?: string;
  market_cap_categories?: string;
  trend_filter?: string;
  earnings_within_days?: number;
  exclude_earnings_within_days?: number;
  min_slope_12w?: number;
  max_slope_12w?: number;
  macd_signals?: string;
  min_open_interest?: number;
  max_open_interest?: number;
  min_baseline_pnl?: number;
  max_baseline_pnl?: number;
  min_dimension1_stars?: number;
  min_dimension2_stars?: number;
  setup_signals?: string;
  strategy?: string;
}

const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  initialFilters = {}
}) => {
  const { strategy } = useStrategy();
  const isCsp = strategy === 'csp';
  // Local filter state
  const [filters, setFilters] = useState<AdvancedFilters>(initialFilters);

  // Distribution data for histograms
  const [distributionData, setDistributionData] = useState<DistributionResponse | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isLoadingDistribution, setIsLoadingDistribution] = useState(false);

  // Preview count
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Initialize filters when modal opens or initialFilters changes
  useEffect(() => {
    if (isOpen) {
      setFilters(initialFilters);
    }
  }, [isOpen, initialFilters]);

  // Fetch distribution data when modal opens
  useEffect(() => {
    if (isOpen && !distributionData) {
      fetchDistributionData();
    }
  }, [isOpen]);

  // Debounced preview count update
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      fetchPreviewCount();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, isOpen, strategy]);

  const fetchDistributionData = async () => {
    setIsLoadingDistribution(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/stocks/screener/distribution`);
      const data = await response.json();
      if (data.status === 'success') {
        setDistributionData(data);
      }
    } catch (error) {
      console.error('Failed to fetch distribution data:', error);
    } finally {
      setIsLoadingDistribution(false);
    }
  };

  const fetchPreviewCount = async () => {
    setIsLoadingPreview(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const params = new URLSearchParams();
      params.append('limit', '1');
      params.append('offset', '0');

      // Add filter params
      const apiParams = convertToApiParams(filters);
      Object.entries(apiParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await fetch(`${API_URL}/api/stocks/screener?${params.toString()}`);
      const data = await response.json();
      if (data.status === 'success') {
        setPreviewCount(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch preview count:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Convert internal filter state to API parameters
  const convertToApiParams = (f: AdvancedFilters): FilterApiParams => {
    const params: FilterApiParams = {};

    // IV Rank
    if (f.iv_rank_min !== undefined && f.iv_rank_min > 0) {
      params.min_iv_rank = f.iv_rank_min;
    }
    if (f.iv_rank_max !== undefined && f.iv_rank_max < 100) {
      params.max_iv_rank = f.iv_rank_max;
    }

    // RSI
    if (f.rsi_min !== undefined && f.rsi_min > 0) {
      params.min_rsi = f.rsi_min;
    }
    if (f.rsi_max !== undefined && f.rsi_max < 100) {
      params.max_rsi = f.rsi_max;
    }

    // Net Margin
    if (f.net_margin_min !== undefined && f.net_margin_min > -50) {
      params.min_net_margin = f.net_margin_min;
    }
    if (f.net_margin_max !== undefined && f.net_margin_max < 50) {
      params.max_net_margin = f.net_margin_max;
    }

    // Debt/Equity
    if (f.debt_equity_max !== undefined && f.debt_equity_max < 5) {
      params.max_debt_equity = f.debt_equity_max;
    }

    // Annualized Yield
    if (f.annualized_yield_min !== undefined && f.annualized_yield_min > 0) {
      params.min_annualized_yield = f.annualized_yield_min;
    }
    if (f.annualized_yield_max !== undefined && f.annualized_yield_max < 100) {
      params.max_annualized_yield = f.annualized_yield_max;
    }

    // Dividend Yield
    if (f.dividend_yield_min !== undefined && f.dividend_yield_min > 0) {
      params.min_dividend_yield = f.dividend_yield_min;
    }
    if (f.dividend_yield_max !== undefined && f.dividend_yield_max < 15) {
      params.max_dividend_yield = f.dividend_yield_max;
    }

    // Spread percentage range
    if (f.spread_pct_min !== undefined && f.spread_pct_min > 0) {
      params.min_spread_pct = f.spread_pct_min;
    }
    if (f.spread_pct_max !== undefined && f.spread_pct_max < 30) {
      params.max_spread_pct = f.spread_pct_max;
    }

    // Sectors
    if (f.sectors && f.sectors.length > 0) {
      params.sectors = f.sectors.join(',');
    }

    // Market Cap Categories
    if (f.market_cap_categories && f.market_cap_categories.length > 0) {
      params.market_cap_categories = f.market_cap_categories.join(',');
    }

    // Trend Signals
    if (f.trend_signals && f.trend_signals.length > 0) {
      params.trend_filter = f.trend_signals.join(',');
    }

    // Same mapping as the screener's own query (StockScreener.handleApplyAdvancedFilters)
    if (f.earnings_within_days) params.earnings_within_days = f.earnings_within_days;
    if (f.exclude_earnings_within_days) params.exclude_earnings_within_days = f.exclude_earnings_within_days;
    if (f.slope_12w_min !== undefined && f.slope_12w_min > -50) params.min_slope_12w = f.slope_12w_min;
    if (f.slope_12w_max !== undefined && f.slope_12w_max < 50) params.max_slope_12w = f.slope_12w_max;
    if (f.macd_signals && f.macd_signals.length > 0) params.macd_signals = f.macd_signals.join(',');
    if (f.open_interest_min !== undefined && f.open_interest_min > 0) params.min_open_interest = f.open_interest_min;
    if (f.open_interest_max !== undefined && f.open_interest_max < OI_SLIDER_MAX) params.max_open_interest = f.open_interest_max;
    if (f.baseline_pnl_min !== undefined && f.baseline_pnl_min > -3) params.min_baseline_pnl = f.baseline_pnl_min;
    if (f.baseline_pnl_max !== undefined && f.baseline_pnl_max < 4) params.max_baseline_pnl = f.baseline_pnl_max;
    if (f.min_dimension1_stars !== undefined) params.min_dimension1_stars = f.min_dimension1_stars;
    if (f.min_dimension2_stars !== undefined) params.min_dimension2_stars = f.min_dimension2_stars;
    if (f.setup_signals && f.setup_signals.length > 0) params.setup_signals = f.setup_signals.join(',');
    if (isCsp) params.strategy = 'csp';

    return params;
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.iv_rank_min !== undefined && filters.iv_rank_min > 0) count++;
    if (filters.iv_rank_max !== undefined && filters.iv_rank_max < 100) count++;
    if (filters.annualized_yield_min !== undefined && filters.annualized_yield_min > 0) count++;
    if (filters.annualized_yield_max !== undefined && filters.annualized_yield_max < 100) count++;
    if (filters.spread_pct_min !== undefined && filters.spread_pct_min > 0) count++;
    if (filters.spread_pct_max !== undefined && filters.spread_pct_max < 30) count++;
    if (filters.market_cap_categories && filters.market_cap_categories.length > 0) count++;
    if (filters.sectors && filters.sectors.length > 0) count++;
    if (filters.net_margin_min !== undefined && filters.net_margin_min > -50) count++;
    if (filters.net_margin_max !== undefined && filters.net_margin_max < 50) count++;
    if (filters.debt_equity_max !== undefined && filters.debt_equity_max < 5) count++;
    if (filters.dividend_yield_min !== undefined && filters.dividend_yield_min > 0) count++;
    if (filters.dividend_yield_max !== undefined && filters.dividend_yield_max < 15) count++;
    if (filters.rsi_min !== undefined && filters.rsi_min > 0) count++;
    if (filters.rsi_max !== undefined && filters.rsi_max < 100) count++;
    if (filters.trend_signals && filters.trend_signals.length > 0) count++;
    if (filters.macd_signals && filters.macd_signals.length > 0) count++;
    if (filters.setup_signals && filters.setup_signals.length > 0) count++;
    if ((filters.slope_12w_min ?? -50) > -50 || (filters.slope_12w_max ?? 50) < 50) count++;
    if ((filters.open_interest_min ?? 0) > 0 || (filters.open_interest_max ?? OI_SLIDER_MAX) < OI_SLIDER_MAX) count++;
    if ((filters.baseline_pnl_min ?? -3) > -3 || (filters.baseline_pnl_max ?? 4) < 4) count++;
    if (filters.min_dimension1_stars !== undefined) count++;
    if (filters.min_dimension2_stars !== undefined) count++;
    if (filters.exclude_earnings_within_days || filters.earnings_within_days) count++;
    return count;
  }, [filters]);

  // Reset to the screener defaults (same as the desktop sidebar's Reset)
  const handleResetAll = () => {
    setFilters({ ...COVERED_CALL_DEFAULT_FILTERS });
  };

  // Apply filters and close
  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="advanced-filter-backdrop open"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="advanced-filter-drawer open">
        {/* Header */}
        <div className="advanced-filter-header">
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', margin: 0 }}>
              Filters
            </h2>
            {activeFilterCount > 0 && (
              <button
                onClick={handleResetAll}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#3B82F6',
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: 0,
                  marginTop: '4px'
                }}
              >
                Reset
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#9CA3AF',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <XMarkIcon style={{ width: '24px', height: '24px' }} />
          </button>
        </div>

        {/* Content: same sections as the desktop sidebar */}
        <div className="advanced-filter-content">
          <FilterSections
            filters={filters}
            update={(updates) => setFilters((prev) => ({ ...prev, ...updates }))}
            distributionData={distributionData}
            isCsp={isCsp}
          />
        </div>

        {/* Footer */}
        <div className="advanced-filter-footer">
          <button
            onClick={handleApply}
            className="apply-filters-button"
          >
            {isLoadingPreview ? (
              'Loading...'
            ) : (
              `Show ${previewCount !== null ? previewCount.toLocaleString() : '...'} Results`
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default AdvancedFilterModal;
